import { useState, useCallback } from 'react';

/**
 * Image handling için custom hook
 * Duplicate image management kodlarını azaltır
 */
export const useImageHandler = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleImageEdit = useCallback((image) => {
    setSelectedImage(image);
    setShowImageEditor(true);
  }, []);

  const handleImageUpdate = useCallback((updatedImage) => {
    setImages(prev => prev.map(img => 
      img.id === updatedImage.id ? updatedImage : img
    ));
    setShowImageEditor(false);
    setSelectedImage(null);
  }, []);

  const handleImageDelete = useCallback((imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  }, []);

  const handleImagePreview = useCallback((image) => {
    const imageIndex = images.findIndex(img => img.id === image.id);
    setCurrentImageIndex(imageIndex);
    setSelectedImage(image);
    setShowImagePreview(true);
  }, [images]);

  const handleImageNavigate = useCallback((newIndex) => {
    setCurrentImageIndex(newIndex);
  }, []);

  const addImages = useCallback((newImages) => {
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const clearImages = useCallback(() => {
    setImages([]);
    setSelectedImage(null);
    setShowImageEditor(false);
    setShowImagePreview(false);
    setCurrentImageIndex(0);
  }, []);

  return {
    images,
    setImages,
    selectedImage,
    setSelectedImage,
    showImageEditor,
    setShowImageEditor,
    showImagePreview,
    setShowImagePreview,
    currentImageIndex,
    setCurrentImageIndex,
    handleImageEdit,
    handleImageUpdate,
    handleImageDelete,
    handleImagePreview,
    handleImageNavigate,
    addImages,
    clearImages
  };
};

/**
 * File upload için custom hook
 * Duplicate file handling kodlarını azaltır
 */
export const useFileUpload = () => {
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const addFiles = useCallback((files) => {
    const validFiles = Array.from(files).filter(file => {
      // Dosya boyutu kontrolü (10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} dosyası çok büyük. Maksimum 10MB olmalıdır.`);
        return false;
      }
      return true;
    });

    const newAttachments = validFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file: file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachments(prev => {
      const attachment = prev.find(att => att.id === id);
      if (attachment && attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(att => att.id !== id);
    });
  }, []);

  const clearAttachments = useCallback(() => {
    // Tüm preview URL'lerini temizle
    attachments.forEach(attachment => {
      if (attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
    });
    setAttachments([]);
  }, [attachments]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  return {
    attachments,
    setAttachments,
    dragActive,
    addFiles,
    removeAttachment,
    clearAttachments,
    handleDrag,
    handleDrop
  };
};
